/**
 * VON Data Service - Frontend API wrapper for all 14 VON sections
 * Handles communication with backend VON endpoints
 */

import { api } from './api';
import {
  Von00Perinatal,
  Von01Maternal,
  Von02Resuscitation,
  Von03RespSupport,
  Von04RespCourse,
  Von05Cardiovascular,
  Von06GiNutrition,
  Von07Renal,
  Von08Neurology,
  Von09Ophthalmology,
  Von09Infections,
  Von11Congenital,
  Von12Genetic,
  Von13Readmission,
  Von10Outcomes,
  VonSummary,
  VonApiResponse
} from '@/types/vonData.types';

class VonDataService {
  // ============================================================================
  // SECTION 00: Perinatal (General Information)
  // ============================================================================
  async getPerinatal(patientId: number): Promise<Von00Perinatal | null> {
    const response = await api.get<VonApiResponse<Von00Perinatal>>(`/api/von/perinatal/${patientId}`);
    return response.data?.data || null;
  }

  async createPerinatal(patientId: number, data: Partial<Von00Perinatal>): Promise<Von00Perinatal> {
    const response = await api.post<VonApiResponse<Von00Perinatal>>(`/api/von/perinatal/${patientId}`, data);
    return response.data?.data!;
  }

  async updatePerinatal(patientId: number, data: Partial<Von00Perinatal>): Promise<Von00Perinatal> {
    const response = await api.put<VonApiResponse<Von00Perinatal>>(`/api/von/perinatal/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 01: Maternal
  // ============================================================================
  async getMaternal(patientId: number): Promise<Von01Maternal | null> {
    const response = await api.get<VonApiResponse<Von01Maternal>>(`/api/von/maternal/${patientId}`);
    return response.data?.data || null;
  }

  async createMaternal(patientId: number, data: Partial<Von01Maternal>): Promise<Von01Maternal> {
    const response = await api.post<VonApiResponse<Von01Maternal>>(`/api/von/maternal/${patientId}`, data);
    return response.data?.data!;
  }

  async updateMaternal(patientId: number, data: Partial<Von01Maternal>): Promise<Von01Maternal> {
    const response = await api.put<VonApiResponse<Von01Maternal>>(`/api/von/maternal/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 02: Resuscitation
  // ============================================================================
  async getResuscitation(patientId: number): Promise<Von02Resuscitation | null> {
    const response = await api.get<VonApiResponse<Von02Resuscitation>>(`/api/von/resuscitation/${patientId}`);
    return response.data?.data || null;
  }

  async createResuscitation(patientId: number, data: Partial<Von02Resuscitation>): Promise<Von02Resuscitation> {
    const response = await api.post<VonApiResponse<Von02Resuscitation>>(`/api/von/resuscitation/${patientId}`, data);
    return response.data?.data!;
  }

  async updateResuscitation(patientId: number, data: Partial<Von02Resuscitation>): Promise<Von02Resuscitation> {
    const response = await api.put<VonApiResponse<Von02Resuscitation>>(`/api/von/resuscitation/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 03: Respiratory Support
  // ============================================================================
  async getRespSupport(patientId: number): Promise<Von03RespSupport | null> {
    const response = await api.get<VonApiResponse<Von03RespSupport>>(`/api/von/resp-support/${patientId}`);
    return response.data?.data || null;
  }

  async createRespSupport(patientId: number, data: Partial<Von03RespSupport>): Promise<Von03RespSupport> {
    const response = await api.post<VonApiResponse<Von03RespSupport>>(`/api/von/resp-support/${patientId}`, data);
    return response.data?.data!;
  }

  async updateRespSupport(patientId: number, data: Partial<Von03RespSupport>): Promise<Von03RespSupport> {
    const response = await api.put<VonApiResponse<Von03RespSupport>>(`/api/von/resp-support/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 04: Respiratory Course
  // ============================================================================
  async getRespCourse(patientId: number): Promise<Von04RespCourse | null> {
    const response = await api.get<VonApiResponse<Von04RespCourse>>(`/api/von/resp-course/${patientId}`);
    return response.data?.data || null;
  }

  async createRespCourse(patientId: number, data: Partial<Von04RespCourse>): Promise<Von04RespCourse> {
    const response = await api.post<VonApiResponse<Von04RespCourse>>(`/api/von/resp-course/${patientId}`, data);
    return response.data?.data!;
  }

  async updateRespCourse(patientId: number, data: Partial<Von04RespCourse>): Promise<Von04RespCourse> {
    const response = await api.put<VonApiResponse<Von04RespCourse>>(`/api/von/resp-course/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 05: Cardiovascular
  // ============================================================================
  async getCardiovascular(patientId: number): Promise<Von05Cardiovascular | null> {
    const response = await api.get<VonApiResponse<Von05Cardiovascular>>(`/api/von/cardiovascular/${patientId}`);
    return response.data?.data || null;
  }

  async createCardiovascular(patientId: number, data: Partial<Von05Cardiovascular>): Promise<Von05Cardiovascular> {
    const response = await api.post<VonApiResponse<Von05Cardiovascular>>(`/api/von/cardiovascular/${patientId}`, data);
    return response.data?.data!;
  }

  async updateCardiovascular(patientId: number, data: Partial<Von05Cardiovascular>): Promise<Von05Cardiovascular> {
    const response = await api.put<VonApiResponse<Von05Cardiovascular>>(`/api/von/cardiovascular/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 06: GI & Nutrition
  // ============================================================================
  async getGiNutrition(patientId: number): Promise<Von06GiNutrition | null> {
    const response = await api.get<VonApiResponse<Von06GiNutrition>>(`/api/von/gi-nutrition/${patientId}`);
    return response.data?.data || null;
  }

  async createGiNutrition(patientId: number, data: Partial<Von06GiNutrition>): Promise<Von06GiNutrition> {
    const response = await api.post<VonApiResponse<Von06GiNutrition>>(`/api/von/gi-nutrition/${patientId}`, data);
    return response.data?.data!;
  }

  async updateGiNutrition(patientId: number, data: Partial<Von06GiNutrition>): Promise<Von06GiNutrition> {
    const response = await api.put<VonApiResponse<Von06GiNutrition>>(`/api/von/gi-nutrition/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 07: Renal
  // ============================================================================
  async getRenal(patientId: number): Promise<Von07Renal | null> {
    const response = await api.get<VonApiResponse<Von07Renal>>(`/api/von/renal/${patientId}`);
    return response.data?.data || null;
  }

  async createRenal(patientId: number, data: Partial<Von07Renal>): Promise<Von07Renal> {
    const response = await api.post<VonApiResponse<Von07Renal>>(`/api/von/renal/${patientId}`, data);
    return response.data?.data!;
  }

  async updateRenal(patientId: number, data: Partial<Von07Renal>): Promise<Von07Renal> {
    const response = await api.put<VonApiResponse<Von07Renal>>(`/api/von/renal/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 08: Neurology
  // ============================================================================
  async getNeurology(patientId: number): Promise<Von08Neurology | null> {
    const response = await api.get<VonApiResponse<Von08Neurology>>(`/api/von/neurology/${patientId}`);
    return response.data?.data || null;
  }

  async createNeurology(patientId: number, data: Partial<Von08Neurology>): Promise<Von08Neurology> {
    const response = await api.post<VonApiResponse<Von08Neurology>>(`/api/von/neurology/${patientId}`, data);
    return response.data?.data!;
  }

  async updateNeurology(patientId: number, data: Partial<Von08Neurology>): Promise<Von08Neurology> {
    const response = await api.put<VonApiResponse<Von08Neurology>>(`/api/von/neurology/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 09: Ophthalmology
  // ============================================================================
  async getOphthalmology(patientId: number): Promise<Von09Ophthalmology | null> {
    const response = await api.get<VonApiResponse<Von09Ophthalmology>>(`/api/von/ophthalmology/${patientId}`);
    return response.data?.data || null;
  }

  async createOphthalmology(patientId: number, data: Partial<Von09Ophthalmology>): Promise<Von09Ophthalmology> {
    const response = await api.post<VonApiResponse<Von09Ophthalmology>>(`/api/von/ophthalmology/${patientId}`, data);
    return response.data?.data!;
  }

  async updateOphthalmology(patientId: number, data: Partial<Von09Ophthalmology>): Promise<Von09Ophthalmology> {
    const response = await api.put<VonApiResponse<Von09Ophthalmology>>(`/api/von/ophthalmology/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 09: Infections
  // ============================================================================
  async getInfections(patientId: number): Promise<Von09Infections | null> {
    const response = await api.get<VonApiResponse<Von09Infections>>(`/api/von/infections/${patientId}`);
    return response.data?.data || null;
  }

  async createInfections(patientId: number, data: Partial<Von09Infections>): Promise<Von09Infections> {
    const response = await api.post<VonApiResponse<Von09Infections>>(`/api/von/infections/${patientId}`, data);
    return response.data?.data!;
  }

  async updateInfections(patientId: number, data: Partial<Von09Infections>): Promise<Von09Infections> {
    const response = await api.put<VonApiResponse<Von09Infections>>(`/api/von/infections/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 11: Congenital Anomalies
  // ============================================================================
  async getCongenital(patientId: number): Promise<Von11Congenital | null> {
    const response = await api.get<VonApiResponse<Von11Congenital>>(`/api/von/congenital/${patientId}`);
    return response.data?.data || null;
  }

  async createCongenital(patientId: number, data: Partial<Von11Congenital>): Promise<Von11Congenital> {
    const response = await api.post<VonApiResponse<Von11Congenital>>(`/api/von/congenital/${patientId}`, data);
    return response.data?.data!;
  }

  async updateCongenital(patientId: number, data: Partial<Von11Congenital>): Promise<Von11Congenital> {
    const response = await api.put<VonApiResponse<Von11Congenital>>(`/api/von/congenital/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 12: Genetic Conditions
  // ============================================================================
  async getGenetic(patientId: number): Promise<Von12Genetic | null> {
    const response = await api.get<VonApiResponse<Von12Genetic>>(`/api/von/genetic/${patientId}`);
    return response.data?.data || null;
  }

  async createGenetic(patientId: number, data: Partial<Von12Genetic>): Promise<Von12Genetic> {
    const response = await api.post<VonApiResponse<Von12Genetic>>(`/api/von/genetic/${patientId}`, data);
    return response.data?.data!;
  }

  async updateGenetic(patientId: number, data: Partial<Von12Genetic>): Promise<Von12Genetic> {
    const response = await api.put<VonApiResponse<Von12Genetic>>(`/api/von/genetic/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 13: Readmission
  // ============================================================================
  async getReadmission(patientId: number): Promise<Von13Readmission | null> {
    const response = await api.get<VonApiResponse<Von13Readmission>>(`/api/von/readmission/${patientId}`);
    return response.data?.data || null;
  }

  async createReadmission(patientId: number, data: Partial<Von13Readmission>): Promise<Von13Readmission> {
    const response = await api.post<VonApiResponse<Von13Readmission>>(`/api/von/readmission/${patientId}`, data);
    return response.data?.data!;
  }

  async updateReadmission(patientId: number, data: Partial<Von13Readmission>): Promise<Von13Readmission> {
    const response = await api.put<VonApiResponse<Von13Readmission>>(`/api/von/readmission/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // SECTION 14: Outcomes & Discharge
  // ============================================================================
  async getOutcomes(patientId: number): Promise<Von10Outcomes | null> {
    const response = await api.get<VonApiResponse<Von10Outcomes>>(`/api/von/outcomes/${patientId}`);
    return response.data?.data || null;
  }

  async createOutcomes(patientId: number, data: Partial<Von10Outcomes>): Promise<Von10Outcomes> {
    const response = await api.post<VonApiResponse<Von10Outcomes>>(`/api/von/outcomes/${patientId}`, data);
    return response.data?.data!;
  }

  async updateOutcomes(patientId: number, data: Partial<Von10Outcomes>): Promise<Von10Outcomes> {
    const response = await api.put<VonApiResponse<Von10Outcomes>>(`/api/von/outcomes/${patientId}`, data);
    return response.data?.data!;
  }

  // ============================================================================
  // Special Methods
  // ============================================================================

  /**
   * Get summary of all 14 sections with completeness information
   */
  async getSummary(patientId: number): Promise<VonSummary | null> {
    const response = await api.get<VonApiResponse<VonSummary>>(`/api/von/summary/${patientId}`);
    return response.data?.data || null;
  }

  /**
   * Delete all VON data for a patient (all 14 sections)
   */
  async deleteAll(patientId: number): Promise<boolean> {
    const response = await api.delete<VonApiResponse<null>>(`/api/von/all/${patientId}`);
    return response.data?.success ?? false;
  }
}

export const vonDataService = new VonDataService();
